import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';

type NotificationType = 'success' | 'error' | 'info';

interface Notification {
  id: number;
  message: string;
  type: NotificationType;
}

interface NotificationContextType {
  addNotification: (message: string, type?: NotificationType) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  let notificationIdCounter = 0;

  const addNotification = useCallback((message: string, type: NotificationType = 'info') => {
    const id = notificationIdCounter++;
    const newNotification: Notification = { id, message, type };
    setNotifications(prev => [...prev, newNotification]);

    // Auto-dismiss after a few seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000); // Dismiss after 5 seconds
  }, []);

  const removeNotification = (id: number) => {
     setNotifications(prev => prev.filter(n => n.id !== id));
  };

  return (
    <NotificationContext.Provider value={{ addNotification }}>
      {children}
      {/* Notification Display Area */}
      <div className="fixed bottom-5 right-5 z-50 space-y-2">
        {notifications.map(notification => (
          <div 
            key={notification.id}
            className={`p-4 rounded-md shadow-lg text-white max-w-sm 
              ${notification.type === 'success' ? 'bg-green-500' : ''}
              ${notification.type === 'error' ? 'bg-red-500' : ''}
              ${notification.type === 'info' ? 'bg-blue-500' : ''}
            `}
          >
            <div className="flex justify-between items-center">
                <span>{notification.message}</span>
                <button onClick={() => removeNotification(notification.id)} className="ml-4 text-xl font-bold leading-none">&times;</button>
            </div>
          </div>
        ))}
      </div>
    </NotificationContext.Provider>
  );
}; 